import React, { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Loader2, Tag, ArrowRight } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CityPicker } from "@/components/CityPicker";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({ agency_name: "", owner_name: "", email: "", phone: "", city: "", password: "", ice: "", rc: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
  }, []);

  const set = (k) => (v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await register(form);
      toast.success("Bienvenue sur link&rent !");
      navigate("/owner");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 lr-gradient-dark relative overflow-hidden">
        <div className="absolute inset-0 lr-grain opacity-50" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <Link to="/" className="self-start inline-block rounded-2xl bg-white p-3">
            <BrandLogo className="h-14" />
          </Link>
          <div>
            <h2 className="font-display text-4xl font-extrabold leading-tight">Rejoignez les loueurs indépendants du Maroc.</h2>
            <p className="mt-4 text-slate-300 max-w-md">Publiez votre parc, gagnez en visibilité et recevez des demandes de location directement. Sans commission cachée.</p>
            <Link to="/pricing" data-testid="register-pricing-link" className="mt-6 inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-sm font-semibold text-white hover:bg-white/20 transition-colors">
              <Tag className="h-4 w-4 text-amber-400" /> Voir les tarifs & abonnements (Standard, Pro, Premium) <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="flex gap-2">
            <span className="h-3 w-8 rounded-sm" style={{ background: "#C1272D" }} />
            <span className="h-3 w-8 rounded-sm" style={{ background: "#006233" }} />
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 py-12">
        <div className="w-full max-w-md">
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Publier mon agence</h1>
          <p className="text-slate-500 mt-2">Créez votre espace loueur en 1 minute.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <input required placeholder="Nom de l'agence" data-testid="reg-agency-name" value={form.agency_name} onChange={(e) => set("agency_name")(e.target.value)} className="h-11 w-full rounded-md border border-input px-3 text-sm" />
            <input required placeholder="Nom du responsable" data-testid="reg-owner-name" value={form.owner_name} onChange={(e) => set("owner_name")(e.target.value)} className="h-11 w-full rounded-md border border-input px-3 text-sm" />
            <input required type="email" placeholder="Email professionnel" data-testid="reg-email" value={form.email} onChange={(e) => set("email")(e.target.value)} className="h-11 w-full rounded-md border border-input px-3 text-sm" />
            <input required placeholder="Téléphone / WhatsApp" data-testid="reg-phone" value={form.phone} onChange={(e) => set("phone")(e.target.value)} className="h-11 w-full rounded-md border border-input px-3 text-sm" />
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="ICE (15 chiffres)" data-testid="reg-ice" value={form.ice} onChange={(e) => set("ice")(e.target.value)} className="h-11 w-full rounded-md border border-input px-3 text-sm" />
              <input placeholder="N° Registre de Commerce" data-testid="reg-rc" value={form.rc} onChange={(e) => set("rc")(e.target.value)} className="h-11 w-full rounded-md border border-input px-3 text-sm" />
            </div>
            <p className="text-xs text-slate-500 -mt-1">Votre ICE et votre RC (équivalents du SIRET) sont vérifiés par notre équipe avant validation de l'agence.</p>
            <CityPicker value={form.city} onChange={set("city")} cities={cities} testid="reg-city" className="h-11" placeholder="Ville principale" />
            <input required type="password" minLength={6} placeholder="Mot de passe (min. 6 caractères)" data-testid="reg-password" value={form.password} onChange={(e) => set("password")(e.target.value)} className="h-11 w-full rounded-md border border-input px-3 text-sm" />
            {error && <p className="text-sm text-red-600" data-testid="register-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="reg-submit-btn" className="w-full h-11 text-white font-semibold" style={{ background: "#006233" }}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer mon espace loueur"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-slate-500 text-center">
            Déjà inscrit ? <Link to="/login" className="font-semibold" style={{ color: "#006233" }}>Se connecter</Link>
            <span className="mx-2 text-slate-300">·</span>
            <Link to="/pricing" data-testid="register-pricing-link-mobile" className="font-semibold" style={{ color: "#C1272D" }}>Voir les tarifs</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
